import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import { cancelSubscription } from '../../services/subscriptionService';

const UserProfileManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile, updateProfile, loading: authLoading, subscription } = useAuth();
  const toast = useToast();
  const [fullName, setFullName] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro subscription?\n\nYou\'ll keep Pro access until the end of your current billing period. After that, your account will revert to the Free plan with limited credits.')) return;
    setCancelling(true);
    try {
      const result = await cancelSubscription();
      toast.success(result?.message || 'Subscription cancelled. You\'ll keep Pro access until the end of your billing period.');
      // Refresh profile to get updated status
      await updateProfile({});
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFullName(userProfile?.full_name || '');
      setGithubUrl(userProfile?.github_url || '');
    }
  }, [userProfile]);

  const handleUpdateProfile = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { error } = await updateProfile({ full_name: fullName, github_url: githubUrl });
      if (error) throw error;
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const displayAvatar = userProfile?.avatar_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture || '';

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const isPro = subscription?.tier === 'pro' || subscription?.tier === 'enterprise';

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Header */}
          <div className="text-center mb-10">
            {/* Avatar */}
            <div className="flex justify-center mb-4">
              {displayAvatar ? (
                <img src={displayAvatar} alt={fullName || 'User'} className="w-20 h-20 rounded-full object-cover border-2 border-border" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-border">
                  <Icon name="User" size={32} className="text-muted-foreground" />
                </div>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
              {userProfile?.full_name || 'Your Profile'}
            </h1>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>

          {/* Content — two cards side by side on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Account Information */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={15} className="text-violet-400" />
                Account Information
              </h2>

              <form onSubmit={handleUpdateProfile} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground text-sm cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    disabled={loading}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">GitHub URL</label>
                  <input
                    type="url"
                    value={githubUrl}
                    onChange={(e) => setGithubUrl(e.target.value)}
                    placeholder="https://github.com/username"
                    disabled={loading}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Used for credibility in AI-generated posts</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 mt-2"
                >
                  {loading ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>

            {/* Subscription */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Crown" size={15} className="text-violet-400" />
                Subscription
              </h2>

              <div className="space-y-4">
                {/* Plan & Status */}
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    isPro ? 'bg-violet-500/15 text-violet-300' : 'bg-muted text-muted-foreground'
                  }`}>
                    {userProfile?.subscription_tier === 'pro' ? 'PRO' : 'BASIC'}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    userProfile?.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-400' :
                    userProfile?.subscription_status === 'cancelled' || userProfile?.subscription_status === 'expired' ? 'bg-red-500/15 text-red-400' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {userProfile?.subscription_status?.toUpperCase() || 'ACTIVE'}
                  </span>
                </div>

                {/* Expiry */}
                {userProfile?.subscription_expires_at && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Expires</p>
                    <p className="text-sm font-medium text-foreground">
                      {new Date(userProfile.subscription_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                )}

                {/* Usage */}
                {subscription?.usage && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Usage This Month</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Generations</span>
                        <span className="font-medium text-foreground">
                          {subscription.usage.ai_generations || 0}
                          {subscription.limits?.ai_generations_per_month > 0 ? ` / ${subscription.limits.ai_generations_per_month}` : ' / Unlimited'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Publishes</span>
                        <span className="font-medium text-foreground">
                          {subscription.usage.publishes || 0}
                          {subscription.limits?.publishes_per_month > 0 ? ` / ${subscription.limits.publishes_per_month}` : ' / Unlimited'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Member since */}
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Member Since</p>
                  <p className="text-sm text-foreground">
                    {userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
                  </p>
                </div>

                {/* Action */}
                <div className="pt-3 border-t border-border space-y-2">
                  {isPro ? (
                    <>
                      <p className="text-xs text-emerald-400 text-center font-medium">
                        You're on the Pro plan. Thank you for your support!
                      </p>
                      {userProfile?.subscription_status !== 'cancelled' && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelling}
                          className="flex items-center justify-center gap-1.5 w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                        >
                          {cancelling ? (
                            <><Icon name="Loader2" size={12} className="animate-spin" /> Cancelling...</>
                          ) : (
                            'Cancel Subscription'
                          )}
                        </button>
                      )}
                      {userProfile?.subscription_status === 'cancelled' && (
                        <p className="text-xs text-muted-foreground text-center">
                          Cancelled — Pro access until {userProfile?.subscription_expires_at ? new Date(userProfile.subscription_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'end of billing period'}
                        </p>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => navigate('/pricing')}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white rounded-lg hover:opacity-90 transition-all"
                    >
                      <Icon name="Zap" size={14} /> Upgrade to Pro
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileManagement;
